import type { PaginatorProps } from '@shared/ui/paginator/types';
import classnames from 'classnames';
import { forwardRef, type ReactElement, type TableHTMLAttributes, useMemo, useRef } from 'react';

import { TableContext } from '../table-context';
import type { ColumnConfiguration } from '../table-context/types';
import styles from './table.module.scss';
import { findAllHeadCellsProps } from './utils';
import { ScrollContainer } from '@shared/ui/scroll-container';
import { Paginator } from '@shared/ui/paginator';
import { Flex, Spin } from 'antd';

export type TableProps = TableHTMLAttributes<HTMLTableElement> & {
  children: ReactElement | ReactElement[];
  isLoading?: boolean;
  hasPaginator?: boolean;
  paginator?: PaginatorProps;
  maxHeight?: string;
};

export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ children, isLoading, hasPaginator = false, paginator, style, maxHeight, ...props }, ref) => {
    const columnsConfiguration: ColumnConfiguration[] = useMemo(
      () =>
        findAllHeadCellsProps(children).map((columnProps, index) => ({
          width: columnProps.width,
          index,
        })),
      [children],
    );

    const classesPaginator = classnames(styles.paginator);

    const paginatorHeight = 32;

    const outletMaxHeightWithChildren = `calc(100vh - ${paginatorHeight}px)`;

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    return (
      <TableContext.Provider
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value={{
          columnsConfiguration,
        }}
      >
        <ScrollContainer
          ref={scrollContainerRef}
          width="100%"
          maxHeight={maxHeight || outletMaxHeightWithChildren}
        >
          <table
            ref={ref}
            style={style}
            {...props}
            className={classnames(styles.table, props.className)}
          >
            {children}
          </table>
        </ScrollContainer>
        {isLoading && (
          <Flex align="center" justify="center" style={{ height: '100%' }}>
            <Spin />
          </Flex>
        )}

        {hasPaginator && (
          <div className={classesPaginator}>
            <Paginator
              initialPage={paginator?.initialPage}
              pageCount={paginator?.pageCount}
              onPageChange={paginator?.onPageChange}
            />
          </div>
        )}
      </TableContext.Provider>
    );
  },
);
